<template>
    <v-card>
        <v-card-title class="text-h5 bg-grey-lighten-2" primary-title style="display: relative">
            Filters for {{ taskDisplayName }}
        </v-card-title>
        <v-card-text>
            <v-form @submit.prevent="save" class="pt-8">
                <v-row>
                    <v-col
                        cols="12"
                        sm="6"
                        md="4"
                        v-for="formField in formFields"
                        :key="formField.name"
                    >
                        <v-autocomplete
                            v-if="formField.attribute.type === 'entry_relation'"
                            multiple
                            :items="
                                getEntryRelationOptionsForRelation(formField.attribute.relation)
                            "
                            item-title="displayName"
                            item-value="value"
                            :label="formField.name"
                            v-model="formField.value"
                            :rules="getValidationRules(formField)"
                        />
                        <v-autocomplete
                            v-else-if="
                                formField.attribute.type === 'string' &&
                                formField.attribute.widget_type === 'dropdown'
                            "
                            multiple
                            :items="formField.options"
                            :label="formField.name"
                            v-model="formField.value"
                            :rules="getValidationRules(formField)"
                        />
                        <v-text-field
                            v-else-if="
                                formField.attribute.type === 'string' ||
                                formField.attribute.type === 'number'
                            "
                            v-model="formField.value"
                            :label="formField.attribute.name"
                            :rules="getValidationRules(formField)"
                        />
                        <v-autocomplete
                            v-else-if="formField.attribute.type === 'global_relation'"
                            multiple
                            :items="
                                getGlobalRelationOptionsForRelation(formField.attribute.relation)
                            "
                            item-title="displayName"
                            item-value="value"
                            :label="formField.name"
                            v-model="formField.value"
                            :rules="getValidationRules(formField)"
                        />
                        <v-menu
                            v-else-if="formField.attribute.type === 'date'"
                            close-on-content-click
                            transition="scale-transition"
                            min-width="290px"
                        >
                            <template v-slot:activator="{ props }">
                                <v-text-field
                                    readonly
                                    clearable
                                    v-bind="props"
                                    :v-model="formField.value"
                                    :label="formField.attribute.name"
                                    :rules="getValidationRules(formField)"
                                >
                                    <template v-slot:prepend>
                                        <v-icon>$calendar</v-icon>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-date-picker no-title scrollable v-model="formField.value" />
                        </v-menu>
                        <v-text-field
                            v-else-if="formField.attribute.type === 'datetime'"
                            type="datetime-local"
                            v-model="formField.value"
                            :label="formField.attribute.name"
                            :rules="getValidationRules(formField)"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-autocomplete
                            :items="conditionOptions"
                            multiple
                            label="Condition"
                            v-model="selectedConditionIds"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>

        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" :disabled="!isFormValid" @click="save"> Save filters </v-btn>
            <v-btn color="primary" @click="cancel"> Cancel </v-btn>
            <v-btn color="error" @click="clear"> Clear filters </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { defineComponent } from "vue";
import {
    findThingSchema,
    getEntryRelationOptionsForRelation,
    getFilterableAttributes,
    getGlobalRelationOptionsForRelation,
} from "@/lib/thingUtils.ts";
import { findReadoutGroupById, findTaskGroupById, getTaskDisplayName } from "@/lib/taskUtils.js";
import _ from "lodash";
import { buildDataCollectionTasksAPIForExperiment } from "@/services/api.services.ts";
import { withLoadingScreen } from "@/lib/loading.js";

function getValidationRulesForAttribute(attribute) {
    const rules = [];
    if (attribute.type === "number") {
        rules.push((v) => !v || !isNaN(v) || "Value must be a number");
    }
    return rules;
}

export default defineComponent({
    props: {
        dataCollectionTask: {
            type: Object,
            required: true,
        },
        taskGroups: {
            type: Array,
            required: true,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
    },

    watch: {
        dataCollectionTask: {
            handler: function () {
                this.updated();
            },
            immediate: true,
        },
    },

    mounted() {
        this.dataCollectionTasksAPI = buildDataCollectionTasksAPIForExperiment(
            this.experiment.code
        );
    },

    data() {
        return {
            dataCollectionTasksAPI: null,
            taskDisplayName: null,
            readoutGroup: null,
            taskGroup: null,
            thingSchema: null,
            oneTimeAttributesList: [],
            filterableAttributes: [],
            formFields: [],
            attributesFilter: null,
            conditionOptions: [],
            selectedConditionIds: [],
        };
    },

    computed: {
        isFormValid() {
            for (const formField of this.formFields) {
                const rules = formField.rules;
                if (rules && rules.length) {
                    if (!rules.every((rule) => rule(formField.value) === true)) {
                        return false;
                    }
                }
            }
            return true;
        },
    },

    methods: {
        getGlobalRelationOptionsForRelation(relation) {
            return getGlobalRelationOptionsForRelation(relation, this.protocol);
        },
        updated() {
            this.readoutGroup = findReadoutGroupById(
                this.dataCollectionTask.readoutGroupId,
                this.readoutGroups
            );
            this.taskGroup = findTaskGroupById(
                this.dataCollectionTask.taskGroupId,
                this.taskGroups
            );
            this.taskDisplayName = getTaskDisplayName(
                this.dataCollectionTask,
                this.taskGroup,
                this.readoutGroup,
                this.experiment.day0
            );
            this.thingSchema = findThingSchema(this.readoutGroup.thingSchemaId, this.protocol);
            this.oneTimeAttributesList = Object.values(this.thingSchema.oneTimeAttributes);
            this.filterableAttributes = this.buildFilterableAttributes();
            this.attributesFilter = _.get(this.dataCollectionTask, "filter.thing.attributes", []);
            this.updateFormFields();
        },
        updateFormFields() {
            this.formFields = this.filterableAttributes.map((attribute) => {
                const attrFilter = this.findAttributeFilterByName(attribute.name);
                return {
                    attribute,
                    name: attribute.name,
                    options: attribute.options,
                    type: attribute.type,
                    value: attrFilter ? attrFilter.value : null,
                    rules: getValidationRulesForAttribute(attribute),
                };
            });

            const conditions = this.experiment.conditions;
            const sortedConditions = _.sortBy(conditions, (condition) => {
                return condition.number;
            });
            this.conditionOptions = sortedConditions.map((condition) => {
                return {
                    title: `${condition.number} ${condition.name}`,
                    value: condition.code,
                };
            });

            this.selectedConditionIds = _.get(
                this.dataCollectionTask,
                "filter.thing.conditionId",
                null
            );
        },
        getEntryRelationOptionsForRelation(relation) {
            return getEntryRelationOptionsForRelation(relation, this.experiment);
        },
        findAttributeFilterByName(name) {
            return this.attributesFilter.find((attr) => attr.name === name);
        },
        getValidationRules(formField) {
            return formField.rules;
        },
        clear() {
            this.updateFormFields();
        },
        async save() {
            if (!this.isFormValid) {
                return;
            }
            await withLoadingScreen(async () => {
                return this.saveTaskFilter();
            });
        },
        async saveTaskFilter() {
            const updatedAttributesFilter = [];

            for (const formField of this.formFields) {
                if (!formField.value || _.isEmpty(formField.value)) {
                    continue;
                }

                this.upsertAttributeFilter(updatedAttributesFilter, formField);
            }

            const taskToSave = {
                ...this.dataCollectionTask,
                filter: {
                    thing: {
                        conditionId: this.selectedConditionIds || null,
                        attributes: updatedAttributesFilter,
                    },
                },
            };

            const savedTask = await this.dataCollectionTasksAPI.update(taskToSave);
            this.$emit("saved", savedTask);
        },
        upsertAttributeFilter(updatedAttributesFilter, formField) {
            const attrFilter = this.findAttributeFilterByName(formField.name);
            const attribute = formField.attribute;
            const value = attribute.type === "number" ? parseInt(formField.value) : formField.value;
            if (attrFilter) {
                attrFilter.value = value;
                updatedAttributesFilter.push(attrFilter);
            } else {
                updatedAttributesFilter.push({
                    name: formField.name,
                    value: value,
                });
            }
        },
        cancel() {
            this.$emit("closed");
        },
        buildFilterableAttributes() {
            return getFilterableAttributes(this.oneTimeAttributesList);
        },
    },
});
</script>
